import { useMutation } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useNavigate } from 'react-router-dom'

import { MESSAGES } from '../constants/messages'
import { useToast } from '../hooks/use-toast'
import type {
  AxiosHttpClient,
  HttpRequest,
  HttpResponse,
} from '../protocols/http-client.protocol'
import { useAuthStore } from '../stores/auth.store'
import type { ResponseAPI } from '../types/response-api.type';
import { rollbar } from './rollbar.config'

export const useAxiosHttpClient = ({ sendRequest }: AxiosHttpClient) => {
  const { toast } = useToast()
  const {
    user,
    actions: { clearAll },
  } = useAuthStore()
  const navigate = useNavigate()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSuccess = (data: HttpResponse, propsMutation: any) => {
    const { status, message } = data
    switch (status) {
      case 200:
        propsMutation.showSuccess &&
          message &&
          toast.success({
            title: message,
          })
        break
      case 202:
      case 204:
      case 203:
      case 400:
        propsMutation.showInfo &&
          message &&
          toast.info({
            title: message,
          })
        break
      case 401:
        propsMutation.showInfo &&
          message &&
          toast.info({
            title: message,
          })
        clearAll()
        setTimeout(() => navigate('/login'), 3000)
        break
      default:
        handleDefaultError(data)
        break
    }
  }

  const handleDefaultError = (data: HttpResponse) => {
    const { message } = data
    toast.error({
      title: MESSAGES.ERROR_TITLE,
      description: MESSAGES.ERROR_DESCRIPTION,
    })
    rollbar.error(message, { data, user })
  }

  const handleError = (error: AxiosError<ResponseAPI>) => {
    rollbar.error(error?.message, { error, user });

    if (error?.response?.data.message) {
      toast.error({
        title: error.response.data.message,
      });
    } else {
      toast.error({
        title: MESSAGES.ERROR_TITLE,
        description: MESSAGES.ERROR_DESCRIPTION,
      });
    }
  };

  const axiosRequest = useMutation<HttpResponse, AxiosError, HttpRequest>({
    mutationFn: async (data) => await sendRequest({ payload: data.payload }),
    onSuccess: (data, propsMutation) => {
      if (propsMutation) handleSuccess(data, propsMutation)
    },
    onError: handleError,
    retry: 2,
    retryDelay: 1000,
  })

  return { axiosRequest }
}
